import { supabase } from "@/integrations/supabase/client";

const NOTIFICATION_TIMEOUT_MS = 2_500;
const NOTIFICATION_MAX_RETRIES = 2;
const CIRCUIT_BREAKER_THRESHOLD = 4;
const CIRCUIT_BREAKER_COOLDOWN_MS = 30_000;

type NotificationErrorCode =
  | "NOTIFICATIONS_DB_UNAVAILABLE"
  | "NOTIFICATIONS_TIMEOUT"
  | "NOTIFICATIONS_CIRCUIT_OPEN"
  /** A write got no response (network error or abort): it may or may not have been applied. */
  | "NOTIFICATIONS_OUTCOME_UNKNOWN"
  | "NOTIFICATIONS_UNKNOWN";

export class NotificationServiceError extends Error {
  constructor(
    public readonly code: NotificationErrorCode,
    message: string,
    public readonly cause?: unknown,
    public readonly retryable = false,
  ) {
    super(message);
    this.name = "NotificationServiceError";
  }
}

const notificationCircuitState = {
  failures: 0,
  openedAt: 0,
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function shouldResetCircuit() {
  return (
    notificationCircuitState.openedAt > 0 &&
    Date.now() - notificationCircuitState.openedAt > CIRCUIT_BREAKER_COOLDOWN_MS
  );
}

function isRetryableError(error: unknown) {
  if (error instanceof NotificationServiceError) {
    return error.retryable;
  }

  if (typeof error !== "object" || !error) return false;
  const anyError = error as { code?: string; message?: string; status?: number };
  const message = String(anyError.message ?? "").toLowerCase();

  return (
    anyError.code === "57014" ||
    anyError.code === "ETIMEDOUT" ||
    anyError.status === 503 ||
    message.includes("timeout") ||
    message.includes("network") ||
    message.includes("failed to fetch")
  );
}

/** RLS denials, missing table, or schema errors — not worth retrying or tripping the circuit breaker. */
function isPermissionOrSchemaError(error: unknown) {
  if (typeof error !== "object" || !error) return false;
  const anyError = error as { code?: string; message?: string };
  const code = anyError.code ?? "";
  const message = String(anyError.message ?? "").toLowerCase();
  return (
    code === "42501" ||           // insufficient_privilege
    code === "42P01" ||           // undefined_table
    code === "PGRST301" ||        // PostgREST: JWT / RLS
    message.includes("permission denied") ||
    message.includes("does not exist")
  );
}

function isUniqueViolation(error: unknown) {
  if (typeof error !== "object" || !error) return false;
  const anyError = error as { code?: string };
  return anyError.code === "23505";
}

function generateNotificationId() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs = NOTIFICATION_TIMEOUT_MS): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    const timer = setTimeout(() => {
      clearTimeout(timer);
      reject(
        new NotificationServiceError(
          "NOTIFICATIONS_TIMEOUT",
          `Notifications dependency timed out after ${timeoutMs}ms`,
          undefined,
          true,
        ),
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]);
}

function assertCircuitClosed(operationName: string) {
  if (shouldResetCircuit()) {
    notificationCircuitState.failures = 0;
    notificationCircuitState.openedAt = 0;
  }

  if (
    notificationCircuitState.openedAt > 0 &&
    Date.now() - notificationCircuitState.openedAt <= CIRCUIT_BREAKER_COOLDOWN_MS
  ) {
    throw new NotificationServiceError(
      "NOTIFICATIONS_CIRCUIT_OPEN",
      `Notifications service circuit open for ${operationName}`,
      undefined,
      true,
    );
  }
}

async function executeNotificationOperation<T>(
  operationName: string,
  fn: () => Promise<T>,
): Promise<T> {
  assertCircuitClosed(operationName);

  let lastError: unknown = null;
  for (let attempt = 0; attempt <= NOTIFICATION_MAX_RETRIES; attempt += 1) {
    try {
      const result = await withTimeout(fn());
      notificationCircuitState.failures = 0;
      notificationCircuitState.openedAt = 0;
      return result;
    } catch (error) {
      lastError = error;

      // Permission / schema errors should not trip the circuit breaker
      if (isPermissionOrSchemaError(error)) {
        break;
      }

      notificationCircuitState.failures += 1;

      const retryable = isRetryableError(error);
      if (!retryable || attempt === NOTIFICATION_MAX_RETRIES) {
        break;
      }

      const backoff = 150 * 2 ** attempt + Math.floor(Math.random() * 75);
      await sleep(backoff);
    }
  }

  if (notificationCircuitState.failures >= CIRCUIT_BREAKER_THRESHOLD) {
    notificationCircuitState.openedAt = Date.now();
  }

  if (lastError instanceof NotificationServiceError) {
    throw lastError;
  }

  throw new NotificationServiceError(
    "NOTIFICATIONS_DB_UNAVAILABLE",
    `Notifications dependency failed during ${operationName}`,
    lastError,
    isRetryableError(lastError),
  );
}

export interface NotificationActor {
  id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
}

export interface NotificationRecord {
  id: string;
  organization_id: string;
  person_id: string;
  actor_person_id: string | null;
  title: string;
  body: string | null;
  link_url: string | null;
  link_label: string | null;
  notification_type: string | null;
  metadata: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  actor?: NotificationActor | null;
}

export interface PaginatedNotifications {
  data: NotificationRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** A successful count recovery requires the caller to load this page before showing rows. */
  resolvedPage?: number;
  degraded?: boolean;
  errorCode?: NotificationErrorCode;
}

/**
 * Server-side list filters. Applied in the query, so `totalCount`, `totalPages` and each page
 * cover the filtered list (filtering one fetched page client-side shows a partial page and a
 * pager for the unfiltered total). Omitted fields don't filter.
 */
export interface NotificationListFilters {
  /** true: read only; false: unread only. */
  isRead?: boolean;
  /** One `notification_type`, e.g. "mention". */
  notificationType?: string;
}

export async function fetchNotifications({
  organizationId,
  personId,
  page = 1,
  pageSize = 10,
  filters,
}: {
  organizationId: string;
  personId: string;
  page?: number;
  pageSize?: number;
  filters?: NotificationListFilters;
}): Promise<PaginatedNotifications> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  try {
    const readRange = (start: number, end: number) => executeNotificationOperation("fetchNotifications", async () => {
      let query = supabase
        .from("notifications")
        .select(
          "*, actor:people!notifications_actor_person_id_fkey(id, first_name, last_name, photo_url)",
          { count: "exact" }
        )
        .setHeader("x-forge-form-alerts", "in-app")
        .eq("organization_id", organizationId)
        .eq("person_id", personId);
      if (filters?.isRead !== undefined) query = query.eq("is_read", filters.isRead);
      if (filters?.notificationType) query = query.eq("notification_type", filters.notificationType);
      return await query.order("created_at", { ascending: false }).range(start, end);
    });

    let response = await readRange(from, to);
    // PostgREST reports an invalid offset as 416; its SDK drops Content-Range on errors.
    // Recover the exact count with the same tenant/person/filters, without treating the
    // probe's first row as this page. Permission, outage and repeated-range errors stay errors.
    if (response.status === 416 && page > 1) {
      const probe = await readRange(0, 0);
      if (probe.error) {
        response = probe;
      } else if (probe.count === null || !Number.isSafeInteger(probe.count) || probe.count < 0) {
        throw new NotificationServiceError(
          "NOTIFICATIONS_UNKNOWN", "Couldn't determine the notification page", undefined, false,
        );
      } else {
        const totalPages = Math.ceil(probe.count / pageSize);
        const resolvedPage = Math.min(page, Math.max(1, totalPages));
        if (resolvedPage !== page) {
          return { data: [], totalCount: probe.count, page, pageSize, totalPages, resolvedPage };
        }
        // Rows may have arrived during the count probe. One bounded retry reads the
        // original range; another 416 is reported rather than recursively recovering.
        response = await readRange(from, to);
        if (!response.error && (response.count === null || !Number.isSafeInteger(response.count) || response.count < 0)) {
          throw new NotificationServiceError(
            "NOTIFICATIONS_UNKNOWN", "Couldn't determine the notification page", undefined, false,
          );
        }
      }
    }
    const { data, error, count } = response;

    if (error) {
      throw new NotificationServiceError(
        "NOTIFICATIONS_DB_UNAVAILABLE",
        "Failed to fetch notifications",
        error,
        isRetryableError(error),
      );
    }

    const totalCount = count ?? 0;
    return {
      data: (data ?? []) as NotificationRecord[],
      totalCount,
      page,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
      ...(count !== null && Number.isSafeInteger(count) && count >= 0 && page > Math.max(1, Math.ceil(count / pageSize))
        ? { resolvedPage: Math.max(1, Math.ceil(count / pageSize)) }
        : {}),
    };
  } catch (error) {
    if (error instanceof NotificationServiceError && error.retryable) {
      return {
        data: [],
        totalCount: 0,
        page,
        pageSize,
        totalPages: 0,
        degraded: true,
        errorCode: error.code,
      };
    }

    throw error;
  }
}

/**
 * Cap on how many unread rows we fetch for the badge count. The badge UI
 * renders anything above 99 as "99+", so reading more rows is wasted work
 * (100 lets the count exceed 99 and trigger the "99+" rendering).
 */
const UNREAD_COUNT_CAP = 100;

export async function fetchUnreadNotificationsCount({
  organizationId,
  personId,
}: {
  organizationId: string;
  personId: string;
}): Promise<number> {
  try {
    // Count by fetching the actual unread rows (capped) — the SAME query the
    // tray list runs (fetchNotifications: org + person), narrowed to
    // is_read=false. This keeps the bell badge consistent with what the tray
    // displays: if the badge is non-zero, those rows exist and will render.
    //
    // History: an exact head-count here walked every matching row through two
    // OR'd RLS policies and hit statement_timeout (2026-04-16 beta UAT
    // §P0-3). It was swapped for `count: "planned"`, but planner estimates
    // come from table statistics and routinely reported unread notifications
    // that didn't exist — a red badge over an empty tray (2026-06-09 UAT
    // §3.5a). Fetching up to UNREAD_COUNT_CAP real rows bounds the RLS row
    // walk (no timeout) while staying exactly truthful for the badge UX.
    const { data, error } = await executeNotificationOperation("fetchUnreadNotificationsCount", () =>
      supabase
        .from("notifications")
        .select("id")
        .setHeader("x-forge-form-alerts", "in-app")
        .eq("organization_id", organizationId)
        .eq("person_id", personId)
        .eq("is_read", false)
        .order("created_at", { ascending: false })
        .range(0, UNREAD_COUNT_CAP - 1)
    );

    if (error) {
      throw new NotificationServiceError(
        "NOTIFICATIONS_UNKNOWN",
        "Error fetching unread notifications count",
        error,
        isRetryableError(error),
      );
    }

    return (data ?? []).length;
  } catch (error) {
    // Graceful degradation: return 0 instead of crashing the polling loop
    if (
      (error instanceof NotificationServiceError && error.retryable) ||
      isPermissionOrSchemaError(error)
    ) {
      return 0;
    }
    throw error;
  }
}

export async function setNotificationReadStatus({
  organizationId,
  notificationId,
  isRead,
}: {
  organizationId: string;
  notificationId: string;
  isRead: boolean;
}): Promise<NotificationRecord> {
  const { data, error } = await executeNotificationOperation("setNotificationReadStatus", () =>
    supabase
      .from("notifications")
      .update({
        is_read: isRead,
        read_at: isRead ? new Date().toISOString() : null,
      })
      .eq("organization_id", organizationId)
      .eq("id", notificationId)
      .select(
        "*, actor:people!notifications_actor_person_id_fkey(id, first_name, last_name, photo_url)"
      )
      .setHeader("x-forge-form-alerts", "in-app")
      .single()
  );

  if (error) throw error;
  return data as NotificationRecord;
}

/** Marks several of the viewer's notifications read or unread in one update (bulk actions and their Undo). */
export async function setNotificationsReadStatus({
  organizationId,
  notificationIds,
  isRead,
}: {
  organizationId: string;
  notificationIds: string[];
  isRead: boolean;
}): Promise<void> {
  if (notificationIds.length === 0) return;
  // One attempt, with no client-side timeout or retry: a timeout doesn't cancel the request, so
  // racing it (or sending it again) leaves writes in flight that a later Undo can land before.
  // This settles only when the request does, so the caller knows the outcome before it offers
  // Undo.
  assertCircuitClosed("setNotificationsReadStatus");
  let response: { error: unknown; status?: number };
  try {
    response = await supabase
      .from("notifications")
      .update({
        is_read: isRead,
        read_at: isRead ? new Date().toISOString() : null,
      })
      .setHeader("x-forge-form-alerts", "in-app")
      .eq("organization_id", organizationId)
      .in("id", notificationIds);
  } catch (cause) {
    response = { error: cause, status: 0 };
  }
  const { error, status } = response;

  if (!error) {
    notificationCircuitState.failures = 0;
    notificationCircuitState.openedAt = 0;
    return;
  }
  // No response (postgrest-js reports a network error or abort as status 0): the update may or
  // may not have been applied.
  if (status === 0) {
    notificationCircuitState.failures += 1;
    if (notificationCircuitState.failures >= CIRCUIT_BREAKER_THRESHOLD) {
      notificationCircuitState.openedAt = Date.now();
    }
    throw new NotificationServiceError(
      "NOTIFICATIONS_OUTCOME_UNKNOWN",
      "No response to setNotificationsReadStatus; the update may or may not have been applied",
      error,
    );
  }
  throw error;
}

export async function markAllNotificationsRead({
  organizationId,
  personId,
}: {
  organizationId: string;
  personId: string;
}) {
  const { error } = await executeNotificationOperation("markAllNotificationsRead", () =>
    supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .setHeader("x-forge-form-alerts", "in-app")
      .eq("organization_id", organizationId)
      .eq("person_id", personId)
      .eq("is_read", false)
  );

  if (error) throw error;
}

export interface CreateNotificationInput {
  organizationId: string;
  personId: string;
  actorPersonId?: string | null;
  title: string;
  body?: string | null;
  linkUrl?: string | null;
  linkLabel?: string | null;
  notificationType?: string | null;
  metadata?: Record<string, unknown> | null;
}

export async function createNotification(
  input: CreateNotificationInput
): Promise<NotificationRecord> {
  const notificationId = generateNotificationId();

  const { data } = await executeNotificationOperation("createNotification", async () => {
    const response = await supabase
      .from("notifications")
      .insert({
        id: notificationId,
        organization_id: input.organizationId,
        person_id: input.personId,
        actor_person_id: input.actorPersonId ?? null,
        title: input.title,
        body: input.body ?? null,
        link_url: input.linkUrl ?? null,
        link_label: input.linkLabel ?? null,
        notification_type: input.notificationType ?? null,
        metadata: input.metadata ?? null,
      })
      .select(
        "*, actor:people!notifications_actor_person_id_fkey(id, first_name, last_name, photo_url)"
      )
      .setHeader("x-forge-form-alerts", "in-app")
      .single();

    if (response.error && isUniqueViolation(response.error)) {
      const existing = await supabase
        .from("notifications")
        .select(
          "*, actor:people!notifications_actor_person_id_fkey(id, first_name, last_name, photo_url)"
        )
        .setHeader("x-forge-form-alerts", "in-app")
        .eq("id", notificationId)
        .single();

      if (!existing.error && existing.data) {
        return existing;
      }
    }

    if (response.error) {
      throw new NotificationServiceError(
        "NOTIFICATIONS_DB_UNAVAILABLE",
        "Failed to create notification",
        response.error,
        isRetryableError(response.error),
      );
    }

    return response;
  });

  return data as NotificationRecord;
}

export async function createNotifications(
  notifications: CreateNotificationInput[]
): Promise<NotificationRecord[]> {
  if (notifications.length === 0) {
    return [];
  }

  const { data, error } = await executeNotificationOperation("createNotifications", () =>
    supabase
      .from("notifications")
      .insert(
        notifications.map((input) => ({
          organization_id: input.organizationId,
          person_id: input.personId,
          actor_person_id: input.actorPersonId ?? null,
          title: input.title,
          body: input.body ?? null,
          link_url: input.linkUrl ?? null,
          link_label: input.linkLabel ?? null,
          notification_type: input.notificationType ?? null,
          metadata: input.metadata ?? null,
        }))
      )
      .select(
        "*, actor:people!notifications_actor_person_id_fkey(id, first_name, last_name, photo_url)"
      )
      .setHeader("x-forge-form-alerts", "in-app")
  );

  if (error) throw error;

  return (data ?? []) as NotificationRecord[];
}

export const __notificationTestUtils = {
  resetCircuit: () => {
    notificationCircuitState.failures = 0;
    notificationCircuitState.openedAt = 0;
  },
};
